/** M-WF-01 · Origini di "Copia da…" per Compila Orari (usa la funzione Foundation unica). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { COPY_MAPPERS } from "@/foundation/copy";
import { planTimesheetCopy } from "@/foundation/timesheet";

const periodLabel = (p) => new Date(Number(p.slice(0, 4)), Number(p.slice(5, 7)) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
const prevPeriod = (y, m) => (m === 1 ? [y - 1, 12] : [y, m - 1]);

export function useTimesheetCopy({ open, days, meta, employeeId, year, month, employees }) {
  const [periods, setPeriods] = useState([]);
  const cache = useRef({});
  const current = `${year}-${String(month).padStart(2, "0")}`;
  const [py, pm] = prevPeriod(year, month);

  useEffect(() => { cache.current = {}; }, [employeeId, year, month]);
  useEffect(() => {
    if (!open || !employeeId) return;
    api.get("/timesheet/periods", { params: { employee_id: employeeId } }).then(({ data }) => setPeriods(data.periods.filter((p) => p !== current))).catch(() => setPeriods([]));
  }, [open, employeeId, current]);

  const fetchDays = useCallback(async (emp, y, m) => {
    const k = `${emp}:${y}-${m}`;
    if (!cache.current[k]) cache.current[k] = (await api.get("/timesheet/copy-source", { params: { employee_id: emp, year: y, month: m } })).data.days;
    if (!cache.current[k].length) throw new Error("L'origine scelta non contiene orari compilati.");
    return cache.current[k];
  }, []);

  const sources = useMemo(() => [
    { id: "prev_day", label: "Giorno precedente", description: "Ogni giornata riprende gli orari del giorno prima, a catena" },
    { id: "prev_week", label: "Settimana precedente", description: "Stesso giorno della settimana prima, a catena" },
    { id: "prev_month", label: "Mese precedente", description: `${periodLabel(`${py}-${String(pm).padStart(2, "0")}`)} · stesso giorno della settimana` },
    { id: "other_month", label: "Altro mese già elaborato", description: "Stesso giorno della settimana (es. 1° lunedì)", choices: periods.map((p) => ({ value: p, label: periodLabel(p) })) },
    { id: "other_employee", label: "Altro dipendente", description: "Stesse date, stesso mese", choices: employees.filter((e) => e.id !== employeeId).map((e) => ({ value: e.id, label: `${e.last_name} ${e.first_name}` })) },
  ], [periods, employees, employeeId, py, pm]);

  const conflictOf = useCallback((date) => (meta[date]?.protected ? "blocked" : meta[date]?.cartellinoHasData ? "dati nel Cartellino" : null), [meta]);

  const buildPlan = useCallback(async ({ source, scope, overwrite, options }) => {
    const base = { days, scope, overwrite, withJustifications: !!options.withJustifications, conflictOf };
    if (source.id === "prev_day") return planTimesheetCopy({ ...base, mapDate: COPY_MAPPERS.offset(1), cascade: true });
    if (source.id === "prev_week") return planTimesheetCopy({ ...base, mapDate: COPY_MAPPERS.offset(7), cascade: true });
    if (source.id === "prev_month") return planTimesheetCopy({ ...base, sourceDays: await fetchDays(employeeId, py, pm), mapDate: COPY_MAPPERS.sameWeekdayIn(py, pm) });
    if (!source.param) throw new Error("Nessuna origine disponibile.");
    if (source.id === "other_month") {
      const [y, m] = source.param.split("-").map(Number);
      return planTimesheetCopy({ ...base, sourceDays: await fetchDays(employeeId, y, m), mapDate: COPY_MAPPERS.sameWeekdayIn(y, m) });
    }
    return planTimesheetCopy({ ...base, sourceDays: await fetchDays(source.param, year, month), mapDate: COPY_MAPPERS.sameDate() });
  }, [days, conflictOf, fetchDays, employeeId, year, month, py, pm]);

  return { sources, buildPlan };
}

export default useTimesheetCopy;
